import React from 'react';
import { useRouteError, Link } from 'react-router-dom';
import clientLogger from '../utils/clientLogger';

const ErrorBoundary = () => {
  const error = useRouteError();
  console.error('[ErrorBoundary] Error caught by boundary:', error);
  console.error('[ErrorBoundary] Current URL:', window.location.href);
  console.error('[ErrorBoundary] Current pathname:', window.location.pathname);

  // Log the error to the server
  clientLogger.logError('react_error_boundary', 'React Error Boundary caught an error', error, {
    action: 'react_component_error',
    component: 'ErrorBoundary',
    route: window.location.pathname
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center px-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-red-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 text-center">
        <h1 className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-600 mb-4">
          Oops!
        </h1>
        <h2 className="text-3xl font-semibold text-gray-200 mb-6">
          Something went wrong
        </h2>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          We encountered an unexpected error. Our team has been notified and we're working to fix it. Please try again later.
        </p>
        <div className="space-y-4">
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
          >
            Return Home
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="block w-full mt-4 text-gray-300 hover:text-white transition-colors duration-200"
          >
            Try Again
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default ErrorBoundary; 